import {
  Component,
  inject,
  Input,
  OnDestroy,
  OnInit,
  signal,
} from '@angular/core';
import { format } from 'date-fns';
import { startWith, Subject, takeUntil } from 'rxjs';
import { CurrencyService } from 'src/app/services/currency/currency.service';
import {
  ClaimForm,
  ClaimFormValue,
  ClaimsForm,
} from 'src/app/services/form/form.controls';
import { FormService } from 'src/app/services/form/form.service';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { PayeeMiniIconButtonComponent } from '../../../../../shared/payee-buttons/payee-mini-icon-button/payee-mini-icon-button.component';
import { PayeeIconComponent } from '../../../../../shared/payee-icon/payee-icon.component';
import { PayeeDropzoneDirective } from '../../../../../shared/payee-dropzone/payee-dropzone.directive';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatIcon } from '@angular/material/icon';
import { MatMenuTrigger, MatMenu } from '@angular/material/menu';
import {
  MatDatepickerInput,
  MatDatepicker,
} from '@angular/material/datepicker';
import { JsonPipe, DecimalPipe } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { CurrencyFlagUrlPipe } from '../../../../../shared/pipes/currency-flag-url/currency-flag-url.pipe';

@Component({
  selector: 'app-claim-form',
  templateUrl: './claim-form.component.html',
  styleUrls: ['./claim-form.component.scss'],
  imports: [
    FormsModule,
    ReactiveFormsModule,
    PayeeMiniIconButtonComponent,
    PayeeIconComponent,
    PayeeDropzoneDirective,
    MatProgressSpinner,
    MatIcon,
    MatMenuTrigger,
    MatMenu,
    MatDatepickerInput,
    MatDatepicker,
    JsonPipe,
    DecimalPipe,
    TranslateModule,
    CurrencyFlagUrlPipe,
  ],
})
export class ClaimFormComponent implements OnInit, OnDestroy {
  private formService = inject(FormService);
  private currencyService = inject(CurrencyService);

  private readonly destroy = new Subject<void>();
  private readonly priorityCurrencies = ['HUF', 'EUR', 'CZK'];

  @Input({ required: true }) form!: ClaimForm;
  @Input({ required: true }) index!: number;

  readonly amountDisplay = signal('');

  readonly ocrLoading = signal(false);

  readonly maxDueDate = new Date(Date.now() - 24 * 60 * 60 * 1000);
  readonly maxDueDateString = format(this.maxDueDate, 'yyyy-MM-dd');

  get claimsForm(): ClaimsForm {
    return this.form.parent as ClaimsForm;
  }
  get value(): ClaimFormValue {
    return this.form.value;
  }

  topCurrencies: string[] = [];
  restCurrencies: string[] = [];

  get original_amount() {
    return this.form.controls.original_amount;
  }
  get due_date_at() {
    return this.form.controls.due_date_at;
  }
  get currency_iso() {
    return this.form.controls.currency_iso;
  }
  get file() {
    return this.form.controls.file;
  }
  get isFileUploaded(): boolean {
    return !(this.file.value instanceof File);
  }

  getFileStorageUrl(): string {
    const file = this.file.value;
    return file && !(file instanceof File) ? file.storage_url : '';
  }

  getFileName(): string {
    const file = this.file.value;
    if (!file) {
      return '';
    }
    return file instanceof File ? file.name : file.file_name;
  }

  get isDeleteable(): boolean {
    if (!this.value.is_deletable) {
      return false;
    }
    return true;
  }
  get isEditable(): boolean {
    return !!this.value.is_editable;
  }

  async ngOnInit(): Promise<void> {
    this.original_amount.valueChanges
      .pipe(takeUntil(this.destroy), startWith(this.original_amount.value))
      .subscribe(() => {
        this.updateAmountDisplay();
      });
    const currencies = await this.currencyService.getAllowedCurrencies();
    this.topCurrencies = this.priorityCurrencies.filter((c) =>
      currencies.includes(c),
    );
    this.restCurrencies = currencies.filter(
      (c) => !this.priorityCurrencies.includes(c),
    );
  }

  ngOnDestroy(): void {
    this.destroy.next();
    this.destroy.complete();
  }

  selectCurrency(iso: string): void {
    this.currency_iso.setValue(iso);
    this.currency_iso.markAsDirty();
  }

  onAmountInput(event: Event): void {
    const target = event.target as HTMLInputElement;
    const n = +target.value.replace(/\D/g, '');
    this.original_amount.markAsDirty();
    if (!Number.isNaN(n) && this.original_amount.value !== n) {
      this.original_amount.patchValue(n);
    } else {
      this.updateAmountDisplay();
    }
  }

  async fileDropped(files: FileList | null): Promise<void> {
    const file = files?.item(0);
    if (!file) {
      return;
    }

    this.ocrLoading.set(true);
    try {
      await this.formService.parseInvoiceFileForClaim(file, this.form);
    } finally {
      this.ocrLoading.set(false);
    }
  }

  onClaimFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    void this.fileDropped(input.files);
    input.value = '';
  }

  removeFile(): void {
    this.file.setValue(null);
    this.file.markAsDirty();
  }

  openLocalFile(): void {
    const file = this.file.value;
    if (!(file instanceof File)) {
      return;
    }

    try {
      const url = URL.createObjectURL(file);
      window.open(url, '_blank');
    } catch (error) {
      console.error('Error while opening file', error);
    }
  }

  async deleteClaim(): Promise<void> {
    const id = this.form.value.id;
    try {
      if (this.claimsForm.length === 1) {
        this.form.patchValue({
          id: '',
          file: null,
          original_amount: 0,
          currency_iso: 'HUF',
          due_date_at: null,
          issued_at: null,
          invoice_number: null,
        });
        this.form.markAsPristine();
        this.form.markAsUntouched();
      } else {
        this.claimsForm.removeAt(this.index);
      }

      if (id) {
        await this.formService.deleteClaim(id);
      }
    } catch (error) {
      console.error('Error while deleting claim', error);
    }
  }

  private updateAmountDisplay(): void {
    const str = this.original_amount.value ?? '';
    const digits = str.toString().replace(/\D/g, '');
    const n = +digits;
    const formatted = new Intl.NumberFormat('hu', {
      useGrouping: true,
    }).format(n);

    setTimeout(this.amountDisplay.set, 0, n === 0 ? '' : formatted || '');
  }
}
